// Typed client for the plan catalog:
//   GET /api/Plan          — the public line-up (anonymous; it's the pricing page)
//   GET /api/Plan/mine     — the caller's own plan
//   GET /api/Plan/all      — every plan with full limits (admin)
//   PUT /api/Plan/{id}     — rename / reprice / re-limit (admin)
//
// Plans are data, not code. The backend keys everything on the stable `id`; `name` exists only
// to be displayed, so nothing here should ever branch on a plan's NAME.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

export interface PlanHighlights {
  customScanners: number;
  scannerUniverse: number;
  strategies: number;
  liveTrading: boolean;
  backtestMinutesPerMonth: number;
  timeframes: string[];
  batchBacktest: boolean;
  runRetentionDays: number;
  builtInAi: boolean;
  /** What the assistant may do on this plan — a real differentiator, so it's advertised. */
  aiAssistant: boolean;
  aiAuthoring: boolean;
  aiTrading: boolean;
  aiLiveTrading: boolean;
}

export interface PublicPlan {
  id: string;
  name: string;
  description: string;
  rank: number;
  monthlyPriceUsd: number;
  isDefault: boolean;
  /** Whether this plan can actually be bought — a Stripe price is wired up and it isn't free. */
  isPurchasable: boolean;
  highlights: PlanHighlights;
}

export interface PlanLimits {
  maxCustomScanners: number;
  maxScannerUniverse: number;
  maxEnabledStrategies: number;
  liveTradingAllowed: boolean;
  maxPaperAccounts: number;
  backtestCoreMinutesPerMonth: number;
  backtestTimeframes: string[];
  maxBacktestSymbols: number;
  maxBacktestLookbackDays: number;
  batchBacktestAllowed: boolean;
  runRetentionDays: number;
  systemAiAllowed: boolean;
  maxAiSchedules: number;
  minAiScheduleIntervalMinutes: number;
  shareLinksAllowed: boolean;
  aiAssistantAllowed: boolean;
  aiAuthoringAllowed: boolean;
  aiTradingAllowed: boolean;
  aiLiveTradingAllowed: boolean;
  maxAiToolCallsPerDay: number;
}

export interface PlanDetail {
  id: string;
  name: string;
  description: string;
  rank: number;
  isPublic: boolean;
  isDefault: boolean;
  monthlyPriceUsd: number;
  /** The Stripe Price this plan sells as. Admin-only; blank means "not for sale". */
  stripePriceId: string | null;
  isPurchasable: boolean;
  updatedAt: string;
  limits: PlanLimits;
}

/** `int.MaxValue` from the server — the "no ceiling" sentinel. */
export const UNLIMITED = 2147483647;

export function formatLimit(value: number): string {
  return value === UNLIMITED ? 'Unlimited' : value.toLocaleString();
}

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.error ?? `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export const planService = {
  listPublic: async (): Promise<PublicPlan[]> => {
    const res = await apiFetch(`${API_BASE}/api/Plan`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<PublicPlan[]>;
  },

  mine: async (): Promise<PlanDetail> => {
    const res = await apiFetch(`${API_BASE}/api/Plan/mine`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<PlanDetail>;
  },

  listAll: async (): Promise<PlanDetail[]> => {
    const res = await apiFetch(`${API_BASE}/api/Plan/all`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<PlanDetail[]>;
  },

  upsert: async (planId: string, body: Partial<PlanDetail> & { name: string }): Promise<PlanDetail> => {
    const res = await apiFetch(`${API_BASE}/api/Plan/${encodeURIComponent(planId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<PlanDetail>;
  },
};
